import { ApiModel } from '@composable-api/api.model'
import type { Monetary, MonetarySet, TaxPerRate } from '../types/general-data'
import { OrderInvoiceModel } from './order-invoice.model'
import { OrderPaymentMethodModel } from './order-payment-method.model'
import { OrderShippingMethodModel } from './order-shipping-method.model'
import { OrderAddressModel } from './order-address.model'
import { PaymentServiceType } from '../enums/payment'
import { OrderItemModel } from '@simploshop-models/order-item.model'
import type { GetPriceOptions } from '@simploshop-models/product.model'
import { getFormattedPrice } from '../utils/localization'
import type { GetItemsPriceOptions } from '@simploshop-models/cart.model'

interface Attributes {
    [OrderModel.ATTR_ID]: number
    [OrderModel.ATTR_CURRENCY_ID]: string
    [OrderModel.ATTR_CUSTOMER_ID]: number | null    // guest customer has `null` value
    [OrderModel.ATTR_EMAIL]: string
    [OrderModel.ATTR_PRICES]: MonetarySet
    [OrderModel.ATTR_WEIGHT]: Weight | null
    [OrderModel.ATTR_SKU]: string | null
    [OrderModel.ATTR_CODE]: string | null
    [OrderModel.ATTR_EAN]: string | null
    [OrderModel.ATTR_PAID_AT]: string | null
    [OrderModel.ATTR_CANCELLED_AT]: string | null
    [OrderModel.ATTR_STOCK_STATE]: number | null
    [OrderModel.ATTR_CREATED_AT]: string
}

interface Embeds {
    [OrderModel.EMBED_STATE]?: State | null
    [OrderModel.EMBED_SHIPPING]?: OrderShippingMethodModel
    [OrderModel.EMBED_PAYMENT]?: OrderPaymentMethodModel
    [OrderModel.EMBED_TAX_PER_RATE]?: TaxPerRate
    [OrderModel.EMBED_INVOICES]: OrderInvoiceModel[]
    [OrderModel.EMBED_ORDER_ADDRESSES]: OrderAddressModel[]
    [OrderModel.EMBED_ITEMS_PRICE]: Monetary
    [OrderModel.EMBED_TAXED_ITEMS_PRICE]: Monetary
}

export class OrderModel extends ApiModel<Attributes, Embeds> {
    static key = 'OrderModel'

    static readonly ATTR_ID = 'id'
    static readonly ATTR_CURRENCY_ID = 'currency_id'
    static readonly ATTR_CUSTOMER_ID = 'customer_id'
    static readonly ATTR_EMAIL = 'email'
    static readonly ATTR_PRICES = 'prices'
    static readonly ATTR_WEIGHT = 'weight'
    static readonly ATTR_SKU = 'sku'
    static readonly ATTR_CODE = 'code'
    static readonly ATTR_EAN = 'ean'
    static readonly ATTR_PAID_AT = 'paid_at'
    static readonly ATTR_CANCELLED_AT = 'cancelled_at'
    static readonly ATTR_STOCK_STATE = 'stock_state'
    static readonly ATTR_CREATED_AT = 'created_at'

    static readonly EMBED_STATE = 'state'
    static readonly EMBED_SHIPPING = 'shipping'
    static readonly EMBED_PAYMENT = 'payment'
    static readonly EMBED_TAX_PER_RATE = 'tax_per_rate'
    static readonly EMBED_INVOICES = 'invoices'
    static readonly EMBED_ORDER_ADDRESSES = 'order_addresses'
    static readonly EMBED_ITEMS_PRICE = 'items_price'
    static readonly EMBED_TAXED_ITEMS_PRICE = 'taxed_items_price'

    get id() {
        return this._getAttribute(OrderModel.ATTR_ID)
    }

    get currencyId() {
        return this._getAttribute(OrderModel.ATTR_CURRENCY_ID)
    }

    get customerId() {
        return this._getAttribute(OrderModel.ATTR_CUSTOMER_ID)
    }

    get email() {
        return this._getAttribute(OrderModel.ATTR_EMAIL)
    }

    private get prices() {
        return this._getAttribute(OrderModel.ATTR_PRICES)
    }

    get weight() {
        return this._getAttribute(OrderModel.ATTR_WEIGHT)
    }

    get sku() {
        return this._getAttribute(OrderModel.ATTR_SKU)
    }

    get code() {
        return this._getAttribute(OrderModel.ATTR_CODE)
    }

    get ean() {
        return this._getAttribute(OrderModel.ATTR_EAN)
    }

    get paidAt() {
        return this._getAttribute(OrderModel.ATTR_PAID_AT)
    }

    get cancelledAt() {
        return this._getAttribute(OrderModel.ATTR_CANCELLED_AT)
    }

    get stockState() {
        return this._getAttribute(OrderModel.ATTR_STOCK_STATE)
    }

    get createdAt() {
        return this._getAttribute(OrderModel.ATTR_CREATED_AT)
    }

    get state() {
        return this._getEmbed(OrderModel.EMBED_STATE)
    }

    get shipping() {
        return this._getEmbed(OrderModel.EMBED_SHIPPING, OrderShippingMethodModel)
    }

    get payment() {
        return this._getEmbed(OrderModel.EMBED_PAYMENT, OrderPaymentMethodModel)
    }

    get taxPerRate() {
        return this._getEmbed(OrderModel.EMBED_TAX_PER_RATE)
    }

    get invoices() {
        return this._getEmbed(OrderModel.EMBED_INVOICES, OrderInvoiceModel)
    }

    get orderAddresses() {
        return this._getEmbed(OrderModel.EMBED_ORDER_ADDRESSES, OrderAddressModel)
    }

    private get itemsPrice() {
        return this._getEmbed(OrderModel.EMBED_ITEMS_PRICE)
    }

    private get taxedItemsPrice() {
        return this._getEmbed(OrderModel.EMBED_TAXED_ITEMS_PRICE)
    }

    // ---------------------------------------------------------------------------------------------------------------------

    getAmountPerTaxRate(taxRate: number): Monetary | null {
        return this.taxPerRate?.[taxRate]?.tax_amount ?? null
    }

    getLatestInvoice(): OrderInvoiceModel | null {
        return this.invoices?.slice(-1)[0] ?? null
    }

    isPaid(): boolean {
        return this.paidAt !== null
    }

    isCancelled(): boolean {
        return this.cancelledAt !== null
    }

    isToBePaidLater(): boolean {
        return [
            PaymentServiceType.NONE,
            PaymentServiceType.BANK_TRANSACTION,
            PaymentServiceType.CARRIER,
            PaymentServiceType.PICKUP_PAYMENT,
            PaymentServiceType.INVOICE_PAYMENT,
        ].includes(this.payment?.paymentMethod?.paymentService as any)
    }

    canFileComplaintFor(item: InstanceType<typeof OrderItemModel>) {
        // TODO: add handling based on the order item when BE is ready
        return this.isPaid() && !this.isCancelled()
    }

    getPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.prices?.pair.taxed_price : this.prices?.pair.price) ?? null
    }

    getFormattedPrice(options?: Partial<GetPriceOptions>) {
        return getFormattedPrice(this.getPrice(options))
    }

    /**
     * Get the price of the order items.
     * By default, this includes all items (normal + items bought for credits).
     */
    getItemsPrice(options?: Partial<GetItemsPriceOptions>) {
        // TODO: add support for specifying items type (when BE is implemented too)

        // TODO: this is a temporary implementation until there is a MonetarySet structure for items price too
        const price = options?.includeDiscountCodes !== false
            ? {
                taxed: this.getPrice(),
                untaxed: this.getPrice({ tax: false }),
            }
            : {
                taxed: this.taxedItemsPrice,
                untaxed: this.itemsPrice,
            }

        return (options?.tax !== false
            ? price.taxed
            : price.untaxed) ?? null
    }

    /**
     * Get the formatted price of the order items.
     * By default, this includes all items (normal + items bought for credits).
     */
    getItemsFormattedPrice(options?: Partial<GetItemsPriceOptions>) {
        return getFormattedPrice(this.getItemsPrice(options))
    }
}

// =====================================================================================================================
// TYPESCRIPT TYPE DECLARATIONS
// =====================================================================================================================


interface Weight {
    unit: string
    value: number
}

interface State {
    id: number,
    label: string | null,
    color: string | null
    text_color: string | null
    makes_shipped: number
    makes_cancelled: number
    makes_completed: number
    makes_paid: number
    makes_invoice: number
    is_initial: boolean
    email_type: string | null
}
